import { useCallback, useEffect, useRef, useState, type FocusEvent, type RefObject } from "react";

const CLOSE_DELAY_MS = 200;

enum MenuOpener {
  HOVER = "hover",
  CLICK = "click",
}

type OpenMenu = { id: string; by: MenuOpener };

export const useHeaderMenuController = ({
  rootRef,
  opensOnHover,
  closesOnBlur,
}: {
  rootRef: RefObject<HTMLElement | null>;
  opensOnHover: boolean;
  closesOnBlur: boolean;
}) => {
  const [openMenu, setOpenMenu] = useState<OpenMenu | null>(null);
  const openRef = useRef<OpenMenu | null>(null);
  openRef.current = openMenu;
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const close = useCallback(() => {
    cancelClose();
    setOpenMenu(null);
  }, [cancelClose]);

  const hoverOpen = useCallback(
    (id: string) => {
      cancelClose();
      setOpenMenu((current) => (current?.id === id ? current : { id, by: MenuOpener.HOVER }));
    },
    [cancelClose],
  );

  const toggle = useCallback(
    (id: string) => {
      cancelClose();
      setOpenMenu((current) => (current?.id === id ? null : { id, by: MenuOpener.CLICK }));
    },
    [cancelClose],
  );

  const onSurfaceLeave = useCallback(() => {
    if (!opensOnHover || openRef.current?.by !== MenuOpener.HOVER) return;
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenMenu(null), CLOSE_DELAY_MS);
  }, [opensOnHover, cancelClose]);

  const onSurfaceBlur = useCallback(
    (event: FocusEvent) => {
      if (!closesOnBlur) return;
      const next = event.relatedTarget;
      if (next instanceof Node && !rootRef.current?.contains(next)) close();
    },
    [closesOnBlur, rootRef, close],
  );

  useEffect(() => cancelClose, [cancelClose]);

  const openId = openMenu?.id ?? null;

  useEffect(() => {
    if (!openId) return;

    const onMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const trigger = rootRef.current?.querySelector<HTMLElement>(`[data-menu-trigger="${openId}"]`);
      close();
      trigger?.focus();
    };

    document.addEventListener("mousedown", onMouseDown, { capture: true });
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, { capture: true });
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openId, rootRef, close]);

  return { openId, close, hoverOpen, toggle, onSurfaceEnter: cancelClose, onSurfaceLeave, onSurfaceBlur };
};
